import { migrateFitnessLegacyDraftRaw } from '@/lib/fitnessDraftLegacyMigration';
import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';
import { parseFitnessDraftRestoreRaw } from '@/lib/fitnessDraftRestoreCodec';
import { notifyFitnessDraftWrite } from '@/lib/fitnessDraftWriter';
import { workoutDraftKey } from '@/lib/fitnessDraftKeys';
import { createFitnessDraftPersistenceCore, type FitnessDraftDurableDriver, type FitnessDraftPersistenceStatus, type FitnessDraftCheckpointRead } from '@/lib/fitnessDraftPersistenceCore';

const fail = (code: string): never => { throw new Error(code); };
const driver: FitnessDraftDurableDriver = {
  async getItem(key) {
    if (Capacitor.isNativePlatform()) {
      if (!Capacitor.isPluginAvailable('Preferences')) return fail('fitness_draft_native_unavailable');
      return (await Preferences.get({ key })).value;
    }
    return globalThis.localStorage.getItem(key);
  },
  async setItem(key, value) {
    if (Capacitor.isNativePlatform()) {
      if (!Capacitor.isPluginAvailable('Preferences')) return fail('fitness_draft_native_unavailable');
      await Preferences.set({ key, value }); return;
    }
    globalThis.localStorage.setItem(key, value);
  },
};

/** Session bytes remain the authority for same-context CAS. The independently
 * acknowledged checkpoint is the recovery copy for a new WebView/process. */
export function createFitnessDraftPersistence(options: { driver: FitnessDraftDurableDriver; sessionStorage?: () => Storage; timeoutMs?: number }) {
  const core = createFitnessDraftPersistenceCore(options);
  const session = options.sessionStorage ?? (() => globalThis.sessionStorage);
  const staged = new Map<string, string>(), latest = new Map<string, string>(), generations = new Map<string, number>();
  const reads = new Map<string, Promise<FitnessDraftCheckpointRead>>();
  const hydrations = new Map<string, { guards: Set<() => boolean>; promise: Promise<void> }>();
  const retirements = new Map<string, string>(), retirementAcks = new Map<string, string>();
  const cacheErrors = new Map<string, FitnessDraftPersistenceStatus>();
  const invalidWrites = new Set<string>();
  const listeners = new Set<() => void>();
  const emit = () => { for (const listener of listeners) { try { listener(); } catch { /* An observer cannot fail a storage acknowledgement. */ } } };
  core.subscribe(() => { for (const [owner, value] of cacheErrors) cacheErrors.set(owner, Object.freeze({ ...value, revision: core.getStatus(owner).revision })); emit(); });
  const guard = (current: () => boolean) => { if (!current()) fail('fitness_draft_owner_changed'); };
  const cache = (owner: string, raw: string | null) => {
    try {
      const storage = session(), key = workoutDraftKey(owner);
      if (raw === null) storage.removeItem(key); else storage.setItem(key, raw);
      if (storage.getItem(key) !== raw) fail('fitness_draft_session_unconfirmed');
      cacheErrors.delete(owner); emit();
    } catch (error) {
      cacheErrors.set(owner, Object.freeze({ status: 'failed', revision: core.getStatus(owner).revision, operation: 'write', errorCode: 'fitness_draft_session_unavailable' })); emit(); throw error;
    }
  };
  const drain = (owner: string) => {
    const raw = staged.get(owner); if (raw === undefined) return;
    staged.delete(owner);
    try { core.write(owner, raw); }
    catch { core.reportFailure(owner, 'write', 'fitness_draft_checkpoint_invalid'); }
  };
  const save = (owner: string, raw: string): void => {
    // Validation precedes both cache replacement and any persistence status.
    let draft: ReturnType<typeof parseFitnessDraftRestoreRaw>;
    try { draft = parseFitnessDraftRestoreRaw(raw, owner); }
    catch { staged.delete(owner); invalidWrites.add(owner); core.reportFailure(owner, 'write', 'fitness_draft_checkpoint_invalid'); return; }
    const retiring = retirements.get(owner);
    if (retiring === draft.clientId) { core.reportFailure(owner, 'write', 'fitness_draft_retired'); return; }
    if (retiring && retirementAcks.get(owner) !== retiring) { core.reportFailure(owner, 'write', 'fitness_draft_storage_unconfirmed'); return; }
    invalidWrites.delete(owner); retirements.delete(owner); retirementAcks.delete(owner);
    latest.set(owner, raw); generations.set(owner, (generations.get(owner) ?? 0) + 1);
    core.markPending(owner);
    try { cache(owner, raw); } catch { /* Durable backup still gets a chance; UI retains the cache failure. */ }
    staged.set(owner, raw);
    queueMicrotask(() => drain(owner));
  };
  const flush = async (owner: string, current: () => boolean): Promise<void> => {
    guard(current); if (invalidWrites.has(owner)) fail('fitness_draft_checkpoint_invalid'); drain(owner); await core.flush(owner, current); guard(current);
    const error = cacheErrors.get(owner); if (error) fail(error.errorCode!);
  };
  const ensureHydrated = async (owner: string, current: () => boolean): Promise<void> => {
    guard(current); if (invalidWrites.has(owner)) fail('fitness_draft_checkpoint_invalid'); drain(owner);
    const generation = generations.get(owner) ?? 0;
    const original = session().getItem(workoutDraftKey(owner));
    let read = reads.get(owner);
    if (!read) { read = core.read(owner, () => true); reads.set(owner, read); void read.finally(() => { if (reads.get(owner) === read) reads.delete(owner); }).catch(() => undefined); }
    let checkpoint: FitnessDraftCheckpointRead;
    try { checkpoint = await read; }
    catch (error) {
      guard(current);
      if ((generations.get(owner) ?? 0) !== generation || session().getItem(workoutDraftKey(owner)) !== original) { await flush(owner, current); return; }
      throw error;
    }
    guard(current);
    if ((generations.get(owner) ?? 0) !== generation || session().getItem(workoutDraftKey(owner)) !== original) { await flush(owner, current); return; }
    let legacy: ReturnType<typeof parseFitnessDraftRestoreRaw> | null = null, legacyRaw = original;
    if (original !== null) {
      try {
        const originalValue = JSON.parse(original) as { clientId?: unknown };
        const missingId = !Object.prototype.hasOwnProperty.call(originalValue, 'clientId') || originalValue.clientId === null || originalValue.clientId === '';
        legacyRaw = migrateFitnessLegacyDraftRaw(original, owner, missingId && checkpoint?.kind === 'checkpoint' ? checkpoint.clientId : undefined);
        legacy = parseFitnessDraftRestoreRaw(legacyRaw, owner);
        // Reuse a persisted migration ID only for the exact same validated state.
        // This also covers set succeeding before its readback/callback was lost.
        if (missingId && checkpoint?.kind === 'checkpoint' && JSON.stringify(legacy) !== JSON.stringify(parseFitnessDraftRestoreRaw(checkpoint.raw, owner))) fail('fitness_draft_checkpoint_exists');
      } catch (error) {
        if (error instanceof Error && error.message === 'fitness_draft_checkpoint_exists') throw error;
        if (!checkpoint || checkpoint.kind !== 'checkpoint') fail('fitness_draft_checkpoint_invalid');
      }
    }
    const persistLegacy = async () => {
      core.write(owner, legacyRaw!);
      try { await flush(owner, current); }
      catch (error) {
        guard(current);
        if ((generations.get(owner) ?? 0) !== generation || session().getItem(workoutDraftKey(owner)) !== original) { await flush(owner, current); return; }
        throw error;
      }
      guard(current);
      if ((generations.get(owner) ?? 0) !== generation || session().getItem(workoutDraftKey(owner)) !== original) { await flush(owner, current); return; }
      if (legacyRaw !== original) { notifyFitnessDraftWrite(owner); cache(owner, legacyRaw); }
    };
    if (checkpoint?.kind === 'tombstone') {
      if (legacy && legacy.clientId !== checkpoint.clientId) { await persistLegacy(); return; }
      if (original !== null) { guard(current); notifyFitnessDraftWrite(owner); cache(owner, null); }
      latest.delete(owner); return;
    }
    // A live update in this process is newer than its previous acknowledged copy.
    if (legacy && latest.get(owner) === original) { await persistLegacy(); return; }
    if (checkpoint?.kind === 'checkpoint') {
      if (legacy && legacy.clientId !== checkpoint.clientId) fail('fitness_draft_checkpoint_exists');
      if (core.getStatus(owner).status === 'failed') { legacyRaw = checkpoint.raw; await persistLegacy(); latest.delete(owner); return; }
      if (original !== checkpoint.raw) { guard(current); notifyFitnessDraftWrite(owner); cache(owner, checkpoint.raw); }
      latest.delete(owner); return;
    }
    if (legacy) await persistLegacy();
  };
  const sharedEnsureHydrated = async (owner: string, current: () => boolean): Promise<void> => {
    guard(current);
    let job = hydrations.get(owner);
    if (!job) {
      const guards = new Set([current]);
      const promise = Promise.resolve().then(() => ensureHydrated(owner, () => [...guards].some(candidate => { try { return candidate(); } catch { return false; } })));
      job = { guards, promise }; hydrations.set(owner, job);
      const issued = job;
      void promise.finally(() => { if (hydrations.get(owner) === issued) hydrations.delete(owner); }).catch(() => undefined);
    } else job.guards.add(current);
    try { await job.promise; guard(current); }
    finally { job.guards.delete(current); }
  };
  const retry = async (owner: string, current: () => boolean): Promise<void> => {
    guard(current); if (invalidWrites.has(owner)) fail('fitness_draft_checkpoint_invalid');
    const retiring = retirements.get(owner);
    if (retiring) { await core.retire(owner, retiring, current); guard(current); retirementAcks.set(owner, retiring); return; }
    const raw = latest.get(owner);
    if (raw !== undefined) { cache(owner, raw); core.write(owner, raw); await flush(owner, current); }
    else await sharedEnsureHydrated(owner, current);
  };
  const retire = async (owner: string, clientId: string, current: () => boolean): Promise<void> => {
    guard(current); invalidWrites.delete(owner); retirements.set(owner, clientId); retirementAcks.delete(owner); staged.delete(owner); latest.delete(owner); generations.set(owner, (generations.get(owner) ?? 0) + 1);
    await core.retire(owner, clientId, current); guard(current); retirementAcks.set(owner, clientId);
  };
  const failure = (owner: string, error: unknown) => core.reportFailure(owner, 'hydrate', error instanceof Error && /^fitness_draft_[a-z_]+$/.test(error.message) ? error.message : 'fitness_draft_storage_unavailable');
  return { save, flush, retry, retire,
    ensureHydrated: async (owner: string, current: () => boolean) => { try { await sharedEnsureHydrated(owner, current); } catch (error) { if (current()) failure(owner, error); throw error; } },
    getStatus: (owner: string | null) => owner ? cacheErrors.get(owner) ?? core.getStatus(owner) : core.getStatus(null),
    subscribe: (listener: () => void) => { listeners.add(listener); return () => { listeners.delete(listener); }; },
  };
}
const persistence = createFitnessDraftPersistence({ driver });
export const subscribeFitnessDraftPersistence = persistence.subscribe;
export const getFitnessDraftPersistenceStatus = persistence.getStatus;
export const ensureWorkoutDraftHydrated = persistence.ensureHydrated;
export const retryWorkoutDraftPersistence = persistence.retry;
export const flushWorkoutDraftPersistence = persistence.flush;
export const retireWorkoutDraftCheckpoint = persistence.retire;
export const checkpointWorkoutDraft = persistence.save;
